import type { FC } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { truncateText } from "@jti/fe-common";
import type { MagentoStockStatus } from "@jti/magento-types";
import { useSelector } from "@jti/store/reduxHooks";
import { FormattedPrice, OutOfStock, ProductMiniature, Tooltip } from "@jti/ui";

import styles from "./BundleItem.module.scss";

export type BundleOptionValueProps = {
  prices: {
    initial_price: number;
    presentation_price?: number;
  };
  product_name: string;
  quantity: number;
  imageUrl?: string;
  tooltip_info?: string;
  stock_status?: MagentoStockStatus;
};

const BundleOptionValue: FC<BundleOptionValueProps> = ({
  product_name,
  quantity,
  prices: { initial_price, presentation_price = null },
  imageUrl,
  tooltip_info,
  stock_status,
}) => {
  const config = useConfig();
  const { currency: shopCurrency } = useSelector((state) => state.cart);
  const maxTooltipLength = 130;

  return (
    <li className={styles.bundleOption} data-testid="bundleProductOption">
      {imageUrl && <ProductMiniature imageUrl={imageUrl} showTitle={false} name={product_name} />}

      <div className={styles.bundleOptionHeader}>
        <strong className={styles.bundleOptionName}>
          <span className={styles.bundleOptionQuantity}>{quantity}&times;</span>
          {product_name}
          {tooltip_info && (
            <div className={styles.tooltipIcon} data-testid="bundleProductOptionTooltip">
              <Tooltip
                content={<span>{truncateText(tooltip_info, maxTooltipLength)}</span>}
                size="small"
                withCloseIcon
              />
            </div>
          )}
        </strong>
        {stock_status === "OUT_OF_STOCK" && config.displayBundleSelectedItems && <OutOfStock hideIcon={true} />}
      </div>

      {config.displayBundleSelectedItemsPrices && (
        <div className={styles.bundleOptionPrice} data-testid="bundleProductOptionPrice">
          {presentation_price !== null && initial_price !== presentation_price ? (
            <>
              <FormattedPrice value={presentation_price} currency={shopCurrency.code} />

              <s className={styles.bundleOptionInitialPrice} data-testid="bundleProductOptionInitialPrice">
                <FormattedPrice value={initial_price} currency={shopCurrency.code} />
              </s>
            </>
          ) : (
            <FormattedPrice value={initial_price} currency={shopCurrency.code} />
          )}
        </div>
      )}
    </li>
  );
};

export default BundleOptionValue;
